// completion-chrome — the isolated "completion" surface (completion-activity [3.3], a
// console-Python "fill in the blanks" activity). Host chrome AROUND the unchanged runner:
// the teacher's template loads with the marked lines BLANKED (server-side), and this
// surface renders the code read-only line-by-line on the tweak custom-HTML model (the Ace
// #editor is hidden by main.js). Every locked line is highlighted read-only text; every
// blank line is a locked leading-whitespace prefix + a single-line <input> the student
// types the statement body into (indentation is locked, never typed). A dedicated Check
// reconstructs the full source (original indent + student body, spliced at each blank
// line), runs it client-side, accumulates stdout, and grades it against the shipped golden
// via challenge-grading.js `compareExpectedOutput` (the SAME grader as debug / the Code
// Challenge expected-output checkpoint).
//
//   wire(ctx) — ctx = { core, payload, trinketId, clientsideMode, isOwner, editorApi, doc }
//
// The golden is SHIPPED in #completion-config (the client compare needs it; the same
// peeking precedent as debug/predict) — the surface only ever DISPLAYS a first-differing-
// line diff, never the golden verbatim. Completion is ITERATIVE: after a passing Check the
// blank fields STAY editable and Check STAYS enabled (revise → re-check). On the first
// passing Check the solve is stored via `respond` ({ solved:true, attempts }); `alreadySolved`
// (from a prior response) shows the completed state on re-open WITHOUT locking the fields.
// NO runner-contract change; editor-core untouched.

import { compareExpectedOutput } from './challenge-grading.js';
import { highlightPythonLine } from './highlight-python.js';
import { mountPreviewStrip } from './preview-strip.js';
// tiered-hints (Trello 6WQKXSWl): the shared three-tier hint ladder renderer.
import { renderLadder, usesLadder, buildLadder } from './hint-ladder.js';
// activity-retry-and-close-on-success (Trello 2eajTgm0): the close-on-success prompt.
import { offerClose, offerReturn } from './close-prompt.js';

// A reconstructed program can contain a non-terminating loop (a blank filled with a bad
// condition), so Check bounds the wall-clock like debug — on timeout we stop the run and
// show a friendly "didn't finish" not-yet (never a hang).
var CHECK_TIMEOUT_MS = 15000;

// Read + parse the server-embedded compare config (already '<'-escaped by the handler).
function readConfig(doc) {
  var el = doc.getElementById('completion-config');
  if (!el) { return null; }
  try {
    var cfg = JSON.parse(el.textContent || '{}');
    if (!cfg || typeof cfg !== 'object') { return null; }
    return cfg;
  } catch (e) { return null; }
}

// The main file (prefer main.py, else the first file) from the loaded (blanked) payload.
function mainFile(files) {
  if (!Array.isArray(files) || !files.length) { return null; }
  return files.filter(function (f) { return f && f.name === 'main.py'; })[0] || files[0];
}

// The leading-whitespace prefix of a line (the locked indent).
function leadingWhitespace(line) {
  var m = /^[ \t]*/.exec(line);
  return m ? m[0] : '';
}

// completion-blank-prefix: the locked start of a blank line, or '' (no prefix / not a string).
function prefixOf(blank) {
  return (blank && typeof blank.prefix === 'string') ? blank.prefix : '';
}

var IDENT_CHAR = /[A-Za-z0-9_]/;

// completion-blank-prefix: pure reassembly of one blank line = indent + prefix + body.
// The body is the student's input with leading whitespace stripped (as before). A duplicate
// of the locked prefix the student typed anyway is dropped ONLY when the trimmed prefix ends
// in an identifier character AND the body starts with it followed by end-of-string or a
// non-identifier character (so `return return x` cannot happen but `returned_value` and
// `x == 5` after a `x =` prefix are untouched). No prefix = byte-for-byte the old line.
export function assembleBlankLine(indent, prefix, rawBody) {
  var body = String(rawBody == null ? '' : rawBody).replace(/^\s+/, '');
  var pre = typeof prefix === 'string' ? prefix : '';
  var t = pre.trim();
  if (t && IDENT_CHAR.test(t.charAt(t.length - 1)) && body.indexOf(t) === 0) {
    var next = body.charAt(t.length);
    if (next === '' || !IDENT_CHAR.test(next)) {
      body = body.slice(t.length).replace(/^\s+/, '');
    }
  }
  return indent + pre + body;
}

// completion-blank-prefix: the input's accessible name (forms mode announces only this).
export function blankAriaLabel(lineNo, blank) {
  var pre = prefixOf(blank).trim();
  return 'Blank on line ' + lineNo
    + (pre ? ', after "' + pre + '"' : '')
    + (blank && blank.placeholder ? ' (' + blank.placeholder + ')' : '');
}

// completion-empty-blank-check: the blank lines whose typed value is empty (trim() === '';
// null/undefined count as empty). Judged on the RAW typed input, never the assembled body.
// Returns line numbers, de-duplicated and ascending.
export function findEmptyBlankLines(blanks, valueOf) {
  var seen = {};
  var out = [];
  (Array.isArray(blanks) ? blanks : []).forEach(function (b) {
    if (!b) { return; }
    var line = b.line;
    if (seen[line]) { return; }
    var v = valueOf(line);
    if (v == null || String(v).trim() === '') {
      seen[line] = true;
      out.push(line);
    }
  });
  return out.sort(function (a, b) { return a - b; });
}

// completion-empty-blank-check: the message naming the empty lines ('' for none).
export function emptyBlanksMessage(lines) {
  if (!Array.isArray(lines) || !lines.length) { return ''; }
  var list;
  if (lines.length === 1) { list = String(lines[0]); }
  else {
    list = lines.slice(0, -1).join(', ') + ' and ' + lines[lines.length - 1];
  }
  return (lines.length === 1 ? 'Line ' : 'Lines ') + list
    + (lines.length === 1 ? ' is' : ' are')
    + ' still empty. Fill in every blank, then press Check again.';
}

export function wire(ctx) {
  var doc  = ctx.doc;
  var core = ctx.core;
  var cfg  = readConfig(doc);
  if (!cfg) { return; }

  // teacher-preview-split (tasks 9.1/9.3): SERVER-RESOLVED preview mode. `cfg.preview` was
  // set only after the route's pre-handler confirmed `isCourseStaff` (R17), so a student who
  // opens a shared or bookmarked preview-flagged URL sees no strip and keeps persisting
  // exactly as today.
  var previewMode = cfg.preview === true;
  // one-tab-same-tab-launches (Trello bD1oe35m): the activity top bar's preview chip and "Back to
  // class" replace the strip, so it is mounted only when the bar is absent (card 2's guard).
  if (!doc.getElementById('surface-topbar-back')) {
    mountPreviewStrip(doc, { active: previewMode, exit: 'reload' });
  }

  var panel = doc.getElementById('completion-panel');
  var host  = doc.querySelector('.editor-host') || doc.querySelector('.editor-pane');
  if (!panel || !host) { return; }

  var normalize = (cfg.normalize && typeof cfg.normalize === 'object') ? cfg.normalize : {};
  var canManage = !!cfg.canManage;
  var mode = (ctx.payload && ctx.payload.mode) || ctx.clientsideMode || '';
  var assetBaseUrl = (ctx.payload && ctx.payload.assetBaseUrl) || '';
  var assetManifest = (ctx.payload && ctx.payload.assetManifest) || [];

  // The BLANKED source (what the server shipped) — the reconstruction baseline. The blank
  // lines already carry ONLY their original leading whitespace (blankOutLines emptied the
  // body server-side), so each blank line's text IS its locked indent prefix.
  var baseFiles = Array.isArray(ctx.payload && ctx.payload.files) ? ctx.payload.files : [];
  var main = mainFile(baseFiles);
  var baseName = (main && main.name) || 'main.py';
  var baseLines = String((main && main.content) || '').split('\n');

  // Map 1-indexed blank line number → { placeholder, hint } (skip out-of-range/bad lines,
  // mirroring the server-side blankOutLines defence). The order also drives the hint list.
  var blankByLine = {};
  var blanks = [];
  (Array.isArray(cfg.blanks) ? cfg.blanks : []).forEach(function (b) {
    if (!b) { return; }
    var n = b.line;
    if (typeof n !== 'number' || !isFinite(n) || Math.floor(n) !== n) { return; }
    if (n < 1 || n > baseLines.length) { return; }
    blankByLine[n] = b;
    blanks.push(b);
  });

  // ── surface state ─────────────────────────────────────────────────────────
  var ready      = false;  // runner ready (Check is disabled before this)
  var checking   = false;  // a Check run is in flight
  var submitted  = false;  // the solved response has been POSTed (once)
  var attempts   = 0;      // client-counted number of Checks (sent on the solving POST)

  var stdoutAcc  = '';     // accumulated STDOUT for THIS check run (reset per Check)
  var errorText  = '';     // stderr / error detail for this run
  var inputAsked = false;  // the program requested stdin (non-interactive guidance)
  var timedOut   = false;  // the run was stopped by the wall-clock timeout
  var timer      = null;   // the wall-clock timeout handle

  // The live value of each blank's input, keyed by line number.
  var inputs = {};         // lineNo -> HTMLInputElement

  // ── DOM helper ────────────────────────────────────────────────────────────
  function el(tag, cls, text) {
    var n = doc.createElement(tag);
    if (cls) { n.className = cls; }
    if (text != null) { n.textContent = text; }
    return n;
  }

  // ── code pane (read-only lines + inline blank inputs) ───────────────────────
  var codeEl = el('div', 'completion-code');
  codeEl.setAttribute('aria-label', 'Program code — fill in the highlighted blank lines');
  host.appendChild(codeEl);

  function renderCode() {
    codeEl.textContent = '';
    baseLines.forEach(function (lineText, i) {
      var lineNo = i + 1;
      var blank = blankByLine[lineNo];
      var line = el('div', 'completion-line' + (blank ? ' is-blank' : ''));

      var n = el('span', 'n', String(lineNo));

      var s = el('span', 's');
      if (blank) {
        // Locked indent prefix (the whole blanked line is its leading whitespace) + a
        // single-line input for the statement body. A single-line input structurally
        // prevents a newline that would desync the line-number map.
        var indent = leadingWhitespace(lineText);
        if (indent) {
          var pre = el('span', 'indent', indent);
          s.appendChild(pre);
        }
        // completion-blank-prefix: the locked start of the line, read by screen readers.
        var lockedStart = prefixOf(blank);
        if (lockedStart) { s.appendChild(el('span', 'prefix', lockedStart)); }
        var input = doc.createElement('input');
        input.type = 'text';
        input.className = 'completion-blank';
        input.setAttribute('spellcheck', 'false');
        input.setAttribute('autocomplete', 'off');
        input.setAttribute('autocapitalize', 'off');
        // graded-input-hardening (Trello Vov6hPyT): defensive browser/extension
        // text-prediction opt-out — could not reproduce or confirm the reported corruption
        // in-app, this is best-effort, not a proven fix.
        input.setAttribute('autocorrect', 'off');
        input.setAttribute('data-gramm', 'false');
        input.setAttribute('data-gramm_editor', 'false');
        input.setAttribute('data-enable-grammarly', 'false');
        input.setAttribute('aria-label', blankAriaLabel(lineNo, blank));
        if (blank.placeholder) { input.placeholder = String(blank.placeholder); }
        inputs[lineNo] = input;
        s.appendChild(input);
      } else {
        s.innerHTML = highlightPythonLine(lineText); // escaped output — safe
      }

      line.appendChild(n);
      line.appendChild(s);
      codeEl.appendChild(line);
    });
  }

  renderCode();

  // Reconstruct the full source: for each blank line, the ORIGINAL leading whitespace +
  // the student's input with any leading whitespace they typed stripped (the indent is
  // locked — the student can never mis-indent a correct answer). Returns a files array.
  function reconstructFiles() {
    var lines = baseLines.slice();
    blanks.forEach(function (b) {
      var lineNo = b.line;
      var input = inputs[lineNo];
      var indent = leadingWhitespace(baseLines[lineNo - 1]);
      lines[lineNo - 1] = assembleBlankLine(indent, prefixOf(b), input ? input.value : '');
    });
    return [{ name: baseName, content: lines.join('\n') }];
  }

  // ── panel: heading + Check + result rail ────────────────────────────────────
  var heading = el('h2', 'completion-heading');
  var pencil = doc.createElementNS('http://www.w3.org/2000/svg', 'svg');
  pencil.setAttribute('class', 'completion-icon'); pencil.setAttribute('viewBox', '0 0 24 24');
  pencil.setAttribute('width', '20'); pencil.setAttribute('height', '20'); pencil.setAttribute('fill', 'none');
  pencil.setAttribute('stroke', 'currentColor'); pencil.setAttribute('stroke-width', '2');
  pencil.setAttribute('stroke-linecap', 'round'); pencil.setAttribute('stroke-linejoin', 'round');
  pencil.setAttribute('aria-hidden', 'true');
  // lucide "pencil-line"
  pencil.innerHTML = '<path d="M12 20h9"/>' +
    '<path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4Z"/>';
  heading.appendChild(pencil);
  heading.appendChild(doc.createTextNode('Fill in the blanks'));
  panel.appendChild(heading);
  panel.appendChild(el('p', 'completion-sub',
    'Type into the highlighted blanks to complete the program, then Check it against the output it should print.'));

  var actions = el('div', 'completion-actions');
  var checkBtn = el('button', 'completion-btn completion-btn-check', 'Check my program');
  checkBtn.type = 'button';
  checkBtn.disabled = true;
  var hint = el('p', 'completion-hint', 'Loading runner…');
  hint.id = 'completion-hint';
  hint.setAttribute('aria-live', 'polite');
  actions.appendChild(checkBtn);
  actions.appendChild(hint);
  panel.appendChild(actions);

  // completion-empty-blank-check: the ONLY writer of the hint text and its `is-error` class.
  // Any ordinary write leaves the empty-blank message state.
  var emptyMsgShown = false;  // the "Line N is still empty" message is currently showing
  var hintBeforeMsg = '';     // the hint text to restore when the message goes away
  function setHint(text, isError) {
    hint.textContent = text;
    hint.classList.toggle('is-error', !!isError);
    emptyMsgShown = false;
  }

  function clearBlankMark(input) {
    input.classList.remove('is-empty');
    input.removeAttribute('aria-invalid');
    input.removeAttribute('aria-describedby');
  }
  function clearAllBlankMarks() {
    blanks.forEach(function (b) { if (inputs[b.line]) { clearBlankMark(inputs[b.line]); } });
  }
  function markedLines() {
    return findEmptyBlankLines(blanks, function (l) {
      return inputs[l] && inputs[l].classList.contains('is-empty') ? '' : 'x';
    });
  }

  function showEmptyBlanks(empty) {
    var msg = emptyBlanksMessage(empty);
    var firstInput = null;
    blanks.forEach(function (b) {
      var input = inputs[b.line];
      if (!input) { return; }
      if (empty.indexOf(b.line) === -1) { clearBlankMark(input); return; }
      input.classList.add('is-empty');
      input.setAttribute('aria-invalid', 'true');
      input.setAttribute('aria-describedby', 'completion-hint');
    });
    if (inputs[empty[0]]) { firstInput = inputs[empty[0]]; }
    if (!emptyMsgShown) { hintBeforeMsg = hint.textContent; }
    if (hint.textContent === msg) {
      // identical repeat: clear, then set on the next tick so the live region re-announces
      setHint('');
      setTimeout(function () { setHint(msg, true); emptyMsgShown = true; }, 0);
    } else {
      setHint(msg, true);
      emptyMsgShown = true;
    }
    if (firstInput) { firstInput.focus(); }
  }

  // Typing into a marked blank clears its mark; the message follows the blanks still marked.
  blanks.forEach(function (b) {
    var input = inputs[b.line];
    if (!input) { return; }
    input.addEventListener('input', function () {
      if (input.value.trim() === '') { return; }
      if (!input.classList.contains('is-empty')) { return; }
      clearBlankMark(input);
      if (!emptyMsgShown) { return; }
      var remaining = markedLines();
      if (!remaining.length) { setHint(hintBeforeMsg, false); }
      else { setHint(emptyBlanksMessage(remaining), true); emptyMsgShown = true; }
    });
  });

  if (canManage) {
    panel.appendChild(el('div', 'completion-preview-note',
      'Preview mode — you are a teacher, so nothing you Check here is recorded.'));
  }

  // Result (hidden until the first Check finishes).
  var result = el('div', 'completion-result');
  result.hidden = true;
  var verdict = el('div', 'completion-verdict');
  var diffLabel = el('p', 'completion-tip-label', 'What to look at');
  var diff = el('pre', 'completion-diff');
  var hintsLabel = el('p', 'completion-tip-label', 'Hints');
  var hintsList = el('ul', 'completion-hints');
  var okTip = el('p', 'completion-tip', '');
  var saveStatus = el('p', 'completion-save-status', '');
  result.appendChild(verdict);
  result.appendChild(diffLabel);
  result.appendChild(diff);
  result.appendChild(hintsLabel);
  result.appendChild(hintsList);
  // tiered-hints: when ANY blank carries tier 2 or 3 the activity is in LADDER mode — a sibling
  // after the flat list holds one "Line N" ladder per blank with any tier (a Hint-1-only blank
  // included, as "Hint 1 of 1"), in blank order, built ONCE per page load and reused so reveals
  // are kept across Checks. Otherwise the flat "Hints" list renders exactly as before.
  var ladderMode = blanks.some(usesLadder);
  var ladderWrap = null;
  if (ladderMode) {
    ladderWrap = el('div', 'hl-blanks');
    ladderWrap.hidden = true;
    blanks.forEach(function (b) {
      if (!buildLadder(b).length) { return; }
      var block = el('div', 'hl-blank');
      block.appendChild(el('p', 'hl-blank-head', 'Line ' + b.line));
      block.appendChild(renderLadder(doc, b, {
        renderText: function (target, text) { target.textContent = text; },
        contextLabel: 'line ' + b.line
      }));
      ladderWrap.appendChild(block);
    });
    result.appendChild(ladderWrap);
  }
  result.appendChild(okTip);
  result.appendChild(saveStatus);
  panel.appendChild(result);

  // ── gating ────────────────────────────────────────────────────────────────
  function updateCheckEnabled() {
    // Check runs whenever the runner is ready and no Check is in flight. It STAYS enabled
    // after a solve — completion is iterative (revise → re-check); the solve is sticky
    // server-side so re-Checks are no-ops.
    checkBtn.disabled = !(ready && !checking);
  }

  function clearTimer() { if (timer) { clearTimeout(timer); timer = null; } }

  // ── Check ─────────────────────────────────────────────────────────────────
  checkBtn.addEventListener('click', function () {
    if (!ready || checking) { return; }
    // completion-empty-blank-check: an empty blank never runs, counts an attempt or hits the
    // network — name the empty lines, mark them and stop. The previous result stays as it was.
    var emptyLines = findEmptyBlankLines(blanks, function (l) { return inputs[l] ? inputs[l].value : ''; });
    if (emptyLines.length) { showEmptyBlanks(emptyLines); return; }
    clearAllBlankMarks();
    checking = true;
    attempts += 1;
    // Reset the accumulator + flags so a prior Check's output can't leak in.
    stdoutAcc = ''; errorText = ''; inputAsked = false; timedOut = false;
    updateCheckEnabled();
    setHint('Running your program…');
    clearTimer();
    // Load the reconstructed source into the runner, then run it. Pass the runner `mode`
    // (like tweak-chrome) so a console program runs correctly.
    var files = reconstructFiles();
    core.load({
      source: files[0] ? files[0].content : '',
      files: files,
      assetBaseUrl: assetBaseUrl,
      assetManifest: assetManifest,
      mode: mode
    });
    // A non-terminating reconstruction would never reach a terminal state — the wall-clock
    // timeout stops it and grades whatever it printed as a not-yet.
    timer = setTimeout(function () {
      if (!checking) { return; }
      timedOut = true;
      core.stop();
    }, CHECK_TIMEOUT_MS);
    core.run();
  });

  // ── run listeners ───────────────────────────────────────────────────────────
  core.on('ready', function () {
    ready = true;
    if (emptyMsgShown) { hintBeforeMsg = 'Fill in the blanks, then Check.'; }
    else { setHint('Fill in the blanks, then Check.'); }
    updateCheckEnabled();
  });
  core.on('stdout', function (text) { if (checking) { stdoutAcc += String(text == null ? '' : text); } });
  core.on('stderr', function (text) { if (checking) { errorText += String(text == null ? '' : text); } });
  core.on('error', function (detail) {
    if (!checking) { return; }
    var d = detail && detail.detail ? detail.detail : detail;
    if (d != null) { errorText += String(d); }
  });
  // Completion targets NON-interactive console programs. If the reconstruction parks on
  // input(), editor-core exposes no programmatic stdin send, so stop() the parked run — it
  // terminates as `stopped`, which the state handler grades as a finish.
  core.on('input-request', function () {
    if (!checking) { return; }
    inputAsked = true;
    core.stop();
  });
  core.on('state', function (state) {
    if (!checking) { return; }
    if (state === 'finished' || state === 'stopped'
        || state === 'student-error' || state === 'runner-error') {
      checking = false;
      clearTimer();
      grade(state);
      updateCheckEnabled();
    }
  });

  // ── grade ─────────────────────────────────────────────────────────────────
  function grade(state) {
    // A timeout is a friendly not-yet — a non-terminating program never grades green.
    if (timedOut) {
      showResult(false, "Your program didn't finish — is there a loop that never ends?");
      return;
    }

    // A runner/student error or a stdin park counts as a normal not-yet: grade the
    // accumulated stdout as the actual output (never throw). The grader's diff shows the
    // first line that differs from the golden.
    var res = compareExpectedOutput({
      goldenOutput: cfg.expectedOutput,
      normalize: normalize,
      label: 'Program output'
    }, stdoutAcc);

    var supplementalTip = '';
    if (!res.passed) {
      if (state === 'student-error' || errorText) {
        supplementalTip = 'Your program hit an error before it finished — check the console for the details.';
      }
      else if (inputAsked) {
        supplementalTip = 'This activity grades printed output — it works best with programs that do not ask for keyboard input.';
      }
    }

    showResult(res.passed, res.diff, supplementalTip);
  }

  function showResult(passed, diffText, supplementalTip) {
    verdict.classList.remove('is-complete', 'is-notyet');
    if (passed) {
      verdict.classList.add('is-complete');
      verdict.textContent = '✓ Complete!';
      diffLabel.hidden = true;
      diff.hidden = true;
      diff.textContent = '';
      hintsLabel.hidden = true;
      hintsList.hidden = true;
      hintsList.textContent = '';
      if (ladderWrap) { ladderWrap.hidden = true; }
      okTip.hidden = false;
      okTip.textContent = 'Nice work — your output matches what it should print.';
      setHint('');
    }
    else {
      verdict.classList.add('is-notyet');
      verdict.textContent = '✗ Not quite yet';
      var diffParts = [];
      if (diffText) { diffParts.push(diffText); }
      if (supplementalTip) { diffParts.push(supplementalTip); }
      var diffFull = diffParts.join('\n\n');
      diffLabel.hidden = !diffFull;
      diff.hidden = !diffFull;
      diff.textContent = diffFull || '';
      // Surface EVERY blank's hint together — compareExpectedOutput returns a first-
      // differing OUTPUT line, which has no honest mapping to a single source blank.
      var hintTexts = blanks
        .map(function (b) { return b && b.hint ? String(b.hint) : ''; })
        .filter(function (h) { return h !== ''; });
      hintsList.textContent = '';
      hintTexts.forEach(function (h) { hintsList.appendChild(el('li', null, h)); });
      var hasHints = hintTexts.length > 0;
      hintsLabel.hidden = !hasHints;
      hintsList.hidden = !hasHints;
      // tiered-hints: ladder mode replaces the flat list with the per-blank ladders.
      if (ladderWrap) {
        hintsList.hidden = true;
        hintsList.textContent = '';
        hintsLabel.hidden = !ladderWrap.childNodes.length;
        ladderWrap.hidden = !ladderWrap.childNodes.length;
      }
      okTip.hidden = true;
      okTip.textContent = '';
      setHint('Keep going — adjust the blanks and Check again.');
    }

    result.hidden = false;

    if (passed) { submitSolve(); }
  }

  // ── store the solve (once) ───────────────────────────────────────────────────
  function submitSolve() {
    if (submitted) { return; }
    submitted = true;
    // teacher-preview-split (task 9.2 / D7): CLIENT-graded — this call only STORES. In
    // server-confirmed preview mode nothing may be written, so skip the store entirely; the
    // Check feedback above is identical to what a student sees.
    if (previewMode) { saveStatus.textContent = 'Preview — nothing is saved.'; return; }
    var url = '/api/courses/' + cfg.courseId + '/lessons/' + cfg.lessonId
            + '/materials/' + cfg.materialId + '/respond';
    try {
      fetch(url, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        // per-class-material-progress: stamp the class cohort key (launch URL → cfg) when present.
        body: JSON.stringify(cfg.linkedCourseId
          ? { answers: { solved: true, attempts: attempts }, linkedCourseId: cfg.linkedCourseId }
          : { answers: { solved: true, attempts: attempts } })
      }).then(function (res) {
        // A teacher preview (canManage) is 403'd by the roster course-student guard —
        // expected and silent; nothing is stored.
        if (res && res.ok && !canManage) { saveStatus.textContent = 'Saved to your progress.'; }
        // activity-retry-and-close-on-success: offer the way back only AFTER the store has
        // settled. Closing the tab with this request in flight aborts it and records nothing.
        offerAfterSolve(!!(res && res.ok));
      }).catch(function () { offerAfterSolve(false); });
    } catch (e) { offerAfterSolve(false); }
  }

  // activity-retry-and-close-on-success: never for a teacher (completion carries
  // `cfg.canManage`), and never over a rejected save. Reached only from a Check the student
  // pressed in THIS session — the `alreadySolved` re-open branch below deliberately does not
  // call `submitSolve`, so nothing is ever armed on page load.
  function offerAfterSolve(saved) {
    if (previewMode || canManage) { return; }
    if (!saved) {
      offerReturn({ sameTab: true, warn: true, label: 'We couldn’t save that. Please tell your teacher.' });
      return;
    }
    offerClose({ sameTab: true, label: 'Nice work — you filled in the blanks.' });
  }

  // ── re-open state ────────────────────────────────────────────────────────────
  // Completion is iterative — a prior solve does NOT lock the fields. Show the completed
  // state (a green marker + the saved note) without disabling Check, so the student can
  // revise and re-check. `submitted` stays FALSE so a fresh solve this session still POSTs
  // (the server $setOnInsert keeps it sticky — a redundant POST is a benign no-op).
  if (cfg.alreadySolved) {
    verdict.classList.add('is-complete');
    verdict.textContent = '✓ Complete!';
    diffLabel.hidden = true; diff.hidden = true;
    hintsLabel.hidden = true; hintsList.hidden = true;
    if (ladderWrap) { ladderWrap.hidden = true; }
    okTip.hidden = false;
    okTip.textContent = 'You have already completed this — feel free to revise and Check again.';
    saveStatus.textContent = 'Saved to your progress.';
    result.hidden = false;
  }

  // The runner may already be ready by the time the chrome wires up; reflect that.
  updateCheckEnabled();
}
